import { UtilsService } from 'core/utils/utils.service';
import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { FormBuilder } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { AppInfoService } from 'core/app-info/app-info.service';
import { AppLocalization } from 'src/app/modules/shared/entities/app-localization';
import { DeviceDetectorService } from 'ngx-device-detector';
import { AccountManager } from 'src/app/modules/shared/entities/account-manager.class';
import { AddressService } from 'core/address/address.service';
import { StorageValues } from 'src/app/modules/shared/entities/storage-values.enum';
import { Subscription } from 'rxjs';
import { DashboardTemplateValues } from 'src/app/modules/shared/entities/dashboard-template-values.class';
import { DashboardService } from '../../../services/dashboard.service';

@Component({
  selector: 'app-leads-search',
  templateUrl: './leads-search.component.html',
  styleUrls: ['./leads-search.component.scss'],
  standalone: false
})
export class LeadsSearchComponent implements OnInit, OnDestroy {
  appStrings: AppLocalization;
  isMobile = false;
  isAccountManager = false;
  accountManagerId: string;
  leadsSearchForm: any;
  isSelectState = false;
  accountManagers: { value: string, label: string }[] = [];
  countries: { value: string, label: string }[] = [];
  states: { value: string, label: string }[] = [];
  statuses: { value: number, label: string }[] = [];
  templateValuesState: any[];
  cachedSearchValues: any;
  disableButtons = true;
  buttons = [
    { label: 'Clear all filters', type: 'tertiary', disableEmpty: true },
    { label: 'Search', type: 'primary' },
  ];
  dialogData: any;
  loading = true;
  subs = new Subscription();

  constructor(
    public dialogRef: MatDialogRef<LeadsSearchComponent>,
    private appInfoService: AppInfoService,
    private fb: FormBuilder,
    private dashboardService: DashboardService,
    private utilsService: UtilsService,
    private addressService: AddressService,
    private deviceService: DeviceDetectorService,
    @Inject(MAT_DIALOG_DATA) data,
  ) {
    this.dialogData = data;
  }

  ngOnInit() {
    this.appStrings = this.appInfoService.appStrings;
    this.isMobile = this.deviceService.isMobile();
    this.isAccountManager = this.appInfoService.isAccountManager();
    this.cachedSearchValues = JSON.parse(sessionStorage.getItem(StorageValues.CachedFilters +
      (this.dialogData.isHistory ? StorageValues.LeadsHistoryKey : StorageValues.LeadsKey)));
    this.addressService.getTemplateValues(([countryValues, stateValues]) => {
      this.countries = this.utilsService.mapObjectToSelect(countryValues.Countries, 'Key', 'Value');
      this.templateValuesState = this.addressService.getStateListPerCountry(stateValues);
      this.dashboardService.getStaticValues((templateValues: DashboardTemplateValues) => {
        this.statuses = this.getStatuses(this.utilsService.mapNumberKeyValueOptions(templateValues.LeadActionStatus));
      });
      this.dashboardService.getFollowupAccountManagers((list: AccountManager[]) => {
        this.accountManagers = this.utilsService.mapObjectToSelect(list, 'id', 'name');
        if (this.cachedSearchValues?.countryId) {
          this.setStates(this.cachedSearchValues.countryId);
        }
        this.leadsSearchForm = this.fb.group({
          leadOwnerId: [this.isAccountManager ? this.dialogData.accountManagerId : this.cachedSearchValues?.leadOwnerId ?? null],
          companyName: [this.cachedSearchValues?.companyName ?? ''],
          statusId: [this.cachedSearchValues?.statusId ?? null],
          countryId: [this.cachedSearchValues?.countryId ?? null],
          stateId: [this.cachedSearchValues?.stateId ?? null],
          stateName: [this.cachedSearchValues?.stateName ?? ''],
          city: [this.cachedSearchValues?.city ?? ''],
          displayedSearchValue: [null]
        });
        this.loading = false;

        this.updateButtonsState();
        this.subs.add(this.leadsSearchForm.valueChanges.subscribe(() => {
          this.updateButtonsState();
        }));
      });
    });
  }

  ngOnDestroy() {
    this.subs.unsubscribe();
  }

  updateButtonsState() {
    const form = this.leadsSearchForm.value;
    if (this.isAccountManager) {
      delete form.leadOwnerId;
    }
    this.disableButtons = !this.utilsService.hasAnyValue(form);
  }

  btnCallback(i: number) {
    if (i === 0) {
      if (this.isAccountManager) {
        this.leadsSearchForm.controls.companyName.reset();
        this.leadsSearchForm.controls.countryId.reset();
        this.leadsSearchForm.controls.statusId.reset();
        this.leadsSearchForm.controls.stateId.reset();
        this.leadsSearchForm.controls.stateName.reset();
        this.leadsSearchForm.controls.city.reset();
      } else {
        this.leadsSearchForm.reset();
      }
    } else {
      this.normalizeInputs();
      const displayValue = this.formatDisplayValue();
      this.leadsSearchForm.controls.displayedSearchValue.setValue(displayValue);
      sessionStorage.setItem(StorageValues.CachedFilters +
        (this.dialogData.isHistory ? StorageValues.LeadsHistoryKey : StorageValues.LeadsKey), JSON.stringify(this.leadsSearchForm.value));
      this.dialogRef.close({ performSearch: true, searchDisplay: displayValue });
    }
  }

  formatDisplayValue() {
    let displayValue = '';
    Object.keys(this.leadsSearchForm.value).forEach((item) => {
      const value = this.leadsSearchForm.value[item];
      if (value && value !== '') {
        if (item === 'leadOwnerId') {
          displayValue += this.accountManagers.find(am => am.value === value)?.label + '; ';
        } else if (item === 'statusId') {
          displayValue += this.statuses.find(am => am.value === value)?.label + '; ';
        } else if (item === 'countryId') {
          displayValue += this.countries.find(am => am.value === value)?.label + '; ';
        } else if (item === 'stateId') {
          displayValue += this.states.find(am => am.value === value)?.label + '; ';
        } else if (item !== 'displayedSearchValue') {
          displayValue += value + '; ';
        }
      }
    });
    if (!displayValue.length) {
      return this.appStrings.accounts_search;
    }
    return displayValue;
  }

  setStates(countryId: number) {
    this.states = this.utilsService.mapObjectToSelect(
      this.templateValuesState[countryId] ?? [],
      'stateId',
      'stateName'
    );
    this.isSelectState = this.states.length > 0;
  }

  handleCountryChange() {
    this.setStates(this.leadsSearchForm.controls.countryId.value);
    this.leadsSearchForm.controls.stateId.reset();
    this.leadsSearchForm.controls.stateName.reset();
  }

  private normalizeInputs() {
    const leadOwnerControl = this.leadsSearchForm.controls.leadOwnerId;
    if (leadOwnerControl.value === '' || !this.accountManagers.find(am => am.value === leadOwnerControl.value)) {
      leadOwnerControl.setValue(null);
    }
  }

  private getStatuses(statuses: { value: number, label: any }[]) {
    let displayList = [];
    if (!this.dialogData.isHistory) {
      if (this.isAccountManager) {
        displayList = this.dashboardService.amLeadsStatus;
      } else {
        displayList = this.dashboardService.adminLeadsStatus;
      }
    } else {
      displayList = this.dashboardService.historyLeadsStatus;
    }

    return statuses.filter(status => displayList.includes(status.value));
  }
}
